/**
 * Page RSVP publique — /rsvp/:rsvp_token
 * Pas d'auth, pas de layout app. Mobile-first.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, MailX, CheckCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import InvitationCard from '../components/plan-table/InvitationCard';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fonts (partagées avec InvitationCard)
if (!document.getElementById('rsvp-fonts')) {
  const l = document.createElement('link');
  l.id   = 'rsvp-fonts';
  l.rel  = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Montserrat:wght@400;600;700&display=swap';
  document.head.appendChild(l);
}

const DIET_OPTIONS = [
  { value: 'standard',   label: 'Standard' },
  { value: 'vegetarian', label: 'Végétarien' },
  { value: 'vegan',      label: 'Végan' },
  { value: 'kid',        label: 'Menu enfant' },
  { value: 'halal',      label: 'Halal' },
  { value: 'other',      label: 'Autre (allergie…)' },
];

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PublicRsvpPage() {
  const { rsvp_token } = useParams();
  const [state,  setState]  = useState('loading');  // loading | 404 | form | success
  const [data,   setData]   = useState(null);        // { guest, event }

  // Formulaire local
  const [segStatus,  setSegStatus]  = useState({});
  const [hasPlus,    setHasPlus]    = useState(false);
  const [plusName,   setPlusName]   = useState('');
  const [plusDiet,   setPlusDiet]   = useState('standard');
  const [diet,       setDiet]       = useState('standard');
  const [message,    setMessage]    = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Charger l'invitation
  useEffect(() => {
    if (!rsvp_token) { setState('404'); return; }
    fetch(`${BACKEND}/api/rsvp/${rsvp_token}`)
      .then(r => {
        if (r.status === 404) throw new Error('404');
        return r.json();
      })
      .then(d => {
        setData(d);
        // Pré-remplir depuis les données existantes
        setSegStatus({ ...(d.guest?.seg_status ?? {}) });
        setDiet(d.guest?.diet ?? 'standard');
        const po = d.guest?.plus_one;
        if (po?.name) { setHasPlus(true); setPlusName(po.name); setPlusDiet(po.diet ?? 'standard'); }
        setMessage(d.guest?.rsvp_message ?? '');
        setState('form');
      })
      .catch(() => setState('404'));
  }, [rsvp_token]);

  // Y a-t-il au moins 1 "yes" ?
  const hasAnyYes = Object.values(segStatus).some(v => v === 'yes');

  // Toggle un statut de segment
  const toggleSeg = useCallback((segId, val) => {
    setSegStatus(prev => ({
      ...prev,
      [segId]: prev[segId] === val ? 'pending' : val,
    }));
  }, []);

  // Soumettre
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/rsvp/${rsvp_token}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seg_status:   segStatus,
          diet,
          plus_one:     hasPlus && plusName.trim()
                          ? { name: plusName.trim(), diet: plusDiet }
                          : null,
          rsvp_message: message,
        }),
      });
      if (!res.ok) throw new Error();
      setState('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // Couleurs depuis invitation_design (fallback aux valeurs par défaut)
  const design = data?.event?.invitation_design ?? {};
  const bgCard    = design.bg_color    || '#FAF6EE';
  const fgCard    = design.text_color  || '#3D2F15';
  const accentCol = design.text_color  || '#D4AF37';

  // Pseudo-plan pour InvitationCard
  const pseudoPlan = {
    nom_evenement:    data?.event?.title,
    type_evenement:   data?.event?.type_evenement ?? 'mariage',
    date_evenement:   data?.event?.date,
    lieu:             data?.event?.place,
    organisateur_nom: data?.event?.hosts,
    invitation_design: design,
  };

  // Date de réponse existante
  const alreadyReplied = data?.guest?.rsvp_date
    ? formatDate(data.guest.rsvp_date)
    : null;

  // ── ÉTAT LOADING ──────────────────────────────────────────────────────────
  if (state === 'loading') return (
    <div style={centerStyle}>
      <Loader2 size={32} style={{ color: '#D4AF37', animation: 'spin 1s linear infinite' }} />
      <p style={{ fontFamily:"'Montserrat',sans-serif", color:'#9CA3AF', fontSize:14, marginTop:12 }}>
        Chargement de votre invitation…
      </p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── ÉTAT 404 ──────────────────────────────────────────────────────────────
  if (state === '404') return (
    <div style={centerStyle}>
      <MailX size={60} style={{ color:'#D1D5DB', marginBottom:16 }} />
      <h1 style={playfairTitle}>Invitation introuvable</h1>
      <p style={{ fontFamily:"'Montserrat',sans-serif", color:'#9CA3AF', fontSize:14,
        maxWidth:340, textAlign:'center', lineHeight:1.7, marginBottom:24 }}>
        Ce lien n'est plus valide ou a expiré.
        Contactez la personne qui vous a invité.
      </p>
      <Link to="/" style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12,
        color:'#9CA3AF', textDecoration:'underline' }}>
        Retour à la page d'accueil
      </Link>
    </div>
  );

  // ── ÉTAT SUCCESS ──────────────────────────────────────────────────────────
  if (state === 'success') {
    const confirmed = (data?.event?.segments ?? [])
      .filter(s => segStatus[s.id] === 'yes')
      .map(s => s.name);

    return (
      <div style={{ ...centerStyle, padding:'48px 24px' }}>
        <CheckCircle size={80} style={{ color:'#22C55E', marginBottom:20 }} />
        <h1 style={{ ...playfairTitle, fontSize:32, marginBottom:8 }}>Merci !</h1>
        <p style={{ fontFamily:"'Montserrat',sans-serif", color:'#374151', fontSize:15, marginBottom:8 }}>
          Votre réponse a bien été enregistrée.
        </p>
        {confirmed.length > 0 && (
          <p style={{ fontFamily:"'Montserrat',sans-serif", color:'#9CA3AF', fontSize:13,
            textAlign:'center', lineHeight:1.6, maxWidth:360, marginBottom:28 }}>
            Vous avez confirmé pour :{' '}
            <strong style={{ color:'#374151' }}>{confirmed.join(' et ')}</strong>
          </p>
        )}
        <button
          onClick={() => setState('form')}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px',
            borderRadius:10, border:'1.5px solid #D1D5DB', background:'transparent',
            fontFamily:"'Montserrat',sans-serif", fontSize:13, fontWeight:600,
            color:'#6B7280', cursor:'pointer' }}>
          <RotateCcw size={14} /> Modifier ma réponse
        </button>
      </div>
    );
  }

  // ── ÉTAT FORM (principal) ─────────────────────────────────────────────────
  const segments = data?.event?.segments ?? [];

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg,#FEFAF5)',
      fontFamily:"'Montserrat',sans-serif" }}>
      <div style={{ maxWidth:540, margin:'0 auto', padding:'32px 16px 80px' }}>

        {/* ── Carte d'invitation ── */}
        <InvitationCard
          design={design}
          plan={pseudoPlan}
          guestName={data?.guest?.nom}
          rsvpToken={null}  /* pas de lien RSVP sur la page RSVP elle-même */
        />

        {/* ── Card formulaire RSVP ── */}
        <div style={{ background:'#FFFFFF', borderRadius:20, padding:24,
          marginTop:24, boxShadow:'0 4px 24px rgba(0,0,0,.09)',
          border:'1px solid #F3F4F6' }}>

          {/* Header */}
          <h2 style={{ ...playfairTitle, fontSize:26, marginBottom: alreadyReplied ? 8 : 20 }}>
            Confirmez votre présence
          </h2>

          {/* Badge déjà répondu */}
          {alreadyReplied && (
            <div style={{ padding:'8px 12px', borderRadius:8, marginBottom:20,
              background:'rgba(212,175,55,.08)', border:'1px solid rgba(212,175,55,.25)' }}>
              <p style={{ fontSize:11, color:'#C58B19', margin:0, lineHeight:1.5 }}>
                ✓ Vous avez déjà répondu le <strong>{alreadyReplied}</strong>.
                Vous pouvez modifier votre réponse ci-dessous.
              </p>
            </div>
          )}

          {/* Segments */}
          {segments.map((seg, i) => (
            <div key={seg.id} style={{
              paddingTop:   i > 0 ? 20 : 0,
              marginTop:    i > 0 ? 20 : 0,
              borderTop:    i > 0 ? '1px solid #F3F4F6' : 'none',
            }}>
              <p style={{ fontFamily:"'Playfair Display',serif", fontWeight:700,
                fontSize:18, margin:'0 0 4px', color:'#1F2937' }}>
                {seg.name}
              </p>
              {seg.time && (
                <p style={{ fontSize:13, color:'#9CA3AF', margin:'0 0 14px' }}>
                  {seg.time}
                </p>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {/* Je viens */}
                <button
                  onClick={() => toggleSeg(seg.id, 'yes')}
                  style={{
                    minHeight:56, borderRadius:12, fontSize:15, fontWeight:700,
                    cursor:'pointer', border:'2px solid',
                    transition:'all .15s',
                    borderColor: '#D4AF37',
                    background: segStatus[seg.id] === 'yes' ? '#D4AF37' : 'transparent',
                    color:       segStatus[seg.id] === 'yes' ? '#0B1E20' : '#D4AF37',
                    fontFamily: "'Montserrat',sans-serif",
                    display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                  }}>
                  ✓ Je viens
                </button>

                {/* Je ne peux pas */}
                <button
                  onClick={() => toggleSeg(seg.id, 'no')}
                  style={{
                    minHeight:56, borderRadius:12, fontSize:15, fontWeight:700,
                    cursor:'pointer', border:'2px solid',
                    transition:'all .15s',
                    borderColor: '#EF4444',
                    background: segStatus[seg.id] === 'no' ? '#EF4444' : 'transparent',
                    color:       segStatus[seg.id] === 'no' ? '#FFFFFF' : '#EF4444',
                    fontFamily: "'Montserrat',sans-serif",
                    display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                  }}>
                  ✕ Je ne peux pas
                </button>
              </div>
            </div>
          ))}

          {/* ── Champs additionnels (slide-down si au moins 1 yes) ── */}
          <div style={{
            maxHeight: hasAnyYes ? 500 : 0,
            overflow:  'hidden',
            transition:'max-height .35s ease',
          }}>
            <div style={{ paddingTop:24, borderTop:'1px solid #F3F4F6', marginTop:20 }}>

              {/* Switch +1 */}
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <Switch checked={hasPlus} onCheckedChange={setHasPlus} id="plus-one-rsvp" />
                <label htmlFor="plus-one-rsvp"
                  style={{ fontSize:14, fontWeight:600, cursor:'pointer', color:'#374151' }}>
                  Je viens avec un accompagnant
                </label>
              </div>

              {hasPlus && (
                <div style={{ display:'flex', flexDirection:'column', gap:10,
                  marginBottom:16, paddingLeft:4 }}>
                  <input
                    value={plusName}
                    onChange={e => setPlusName(e.target.value)}
                    placeholder="Nom de l'accompagnant (facultatif)"
                    style={inputSt}
                  />
                  <Select value={plusDiet} onValueChange={setPlusDiet}>
                    <SelectTrigger style={{ fontSize:13, height:40 }}>
                      <SelectValue placeholder="Régime de l'accompagnant" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIET_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Régime */}
              <div style={{ marginBottom:16 }}>
                <label style={labelSt}>Régime alimentaire</label>
                <Select value={diet} onValueChange={setDiet}>
                  <SelectTrigger style={{ fontSize:13, height:40 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIET_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <label style={labelSt}>Note ou message (facultatif)</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Un mot pour les hôtes, allergie, information à transmettre…"
                  style={{ ...inputSt, resize:'vertical', paddingTop:9, paddingBottom:9 }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Bouton de soumission ── */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width:'100%', minHeight:56, marginTop:16,
            borderRadius:14, border:'none', background:'#D4AF37',
            color:'#0B1E20', fontSize:16, fontWeight:700, cursor:'pointer',
            fontFamily:"'Montserrat',sans-serif", letterSpacing:'0.02em',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            boxShadow:'0 8px 24px rgba(212,175,55,.35)',
            opacity: submitting ? 0.7 : 1,
            transition:'opacity .15s',
          }}>
          {submitting
            ? <><Loader2 size={18} style={{ animation:'spin 1s linear infinite' }} /> Envoi en cours…</>
            : 'Confirmer ma réponse'}
        </button>

        {/* Texte discret bas */}
        <p style={{ textAlign:'center', fontSize:11, color:'#C4B5A0', marginTop:16, lineHeight:1.5 }}>
          Votre réponse est sécurisée et accessible uniquement aux organisateurs.
        </p>
      </div>
    </div>
  );
}

// ─── Styles partagés ──────────────────────────────────────────────────────────
const centerStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg,#FEFAF5)', padding: '40px 24px',
  fontFamily: "'Montserrat',sans-serif",
};
const playfairTitle = {
  fontFamily: "'Playfair Display',serif",
  fontWeight: 700, color: '#1F2937', margin: 0,
};
const labelSt = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#374151', marginBottom: 6,
};
const inputSt = {
  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
  border: '1px solid #D1D5DB', outline: 'none', boxSizing: 'border-box',
  fontFamily: "'Montserrat',sans-serif", lineHeight: 1.5,
  background: '#FFFFFF', color: '#1F2937',
};
